// Fast, browser-side stem pre-checks (Phase 4, task 24) — run on file SELECT, before upload.
//
// These are cheap, advisory heads-ups so an obvious mistake is caught at the cheapest possible
// moment. They are NOT the authoritative verdict: that comes post-ingest from the API (ffprobe
// measurements), computed against the whole set. Everything here is non-blocking and worded as a
// question.
//
// Two reads, both free:
//   1. WAV header (first ~4 KB): sample rate + bit depth from the `fmt ` chunk — WAV only.
//   2. File.size: a soft proxy for duration (uncompressed WAV of the same format ≈ same
//      bytes-per-second), so a size well off the set hints at a length mismatch.
//
// Non-WAV files are not header-parsed (their layouts differ); they fall through to the post-ingest
// verdict, and we surface a "we recommend WAV" nudge instead.

export interface WavHeader {
  sampleRateHz: number;
  bitDepth: number;
  channels: number;
}

export function isWav(filename: string): boolean {
  return /\.wav$/i.test(filename);
}

/**
 * Parse sample rate / bit depth / channels from a WAV file's header. Reads only the first slice,
 * so it's instant even for a multi-GB file. Returns null if the bytes aren't a RIFF/WAVE header
 * or the `fmt ` chunk can't be found (e.g. an exotic WAV) — caller treats null as "unknown".
 */
export async function readWavHeader(file: File): Promise<WavHeader | null> {
  try {
    const buf = await file.slice(0, 4096).arrayBuffer();
    const view = new DataView(buf);
    if (buf.byteLength < 12) return null;
    // "RIFF" .... "WAVE"
    const riff = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
    const wave = String.fromCharCode(view.getUint8(8), view.getUint8(9), view.getUint8(10), view.getUint8(11));
    if (riff !== 'RIFF' || wave !== 'WAVE') return null;

    // Walk chunks from offset 12 looking for "fmt ".
    let off = 12;
    while (off + 8 <= buf.byteLength) {
      const id = String.fromCharCode(
        view.getUint8(off), view.getUint8(off + 1), view.getUint8(off + 2), view.getUint8(off + 3)
      );
      const size = view.getUint32(off + 4, true);
      if (id === 'fmt ') {
        if (off + 8 + 16 > buf.byteLength) return null;
        const channels = view.getUint16(off + 8 + 2, true);
        const sampleRateHz = view.getUint32(off + 8 + 4, true);
        const bitDepth = view.getUint16(off + 8 + 14, true);
        if (!sampleRateHz || !bitDepth) return null;
        return { sampleRateHz, bitDepth, channels: channels || 0 };
      }
      // Advance to the next chunk (chunks are word-aligned; pad odd sizes).
      off += 8 + size + (size % 2);
    }
    return null;
  } catch {
    return null;
  }
}

/** A stem already in the set, used as the comparison reference for the pre-check. */
export interface SetStemRef {
  sampleRateHz: number | null;
  bitDepth: number | null;
  channels: number | null;
  durationMs: number | null;
  sizeBytes: number | null; // declared size of the existing stem, if known
}

export interface PrecheckNotice {
  severity: 'warn' | 'info';
  message: string;
}

/** Most common non-null value (mode) — mirrors the API's infer-from-set logic. */
function mode<T>(vals: (T | null | undefined)[]): T | null {
  const counts = new Map<T, number>();
  let best: T | null = null;
  let bestN = 0;
  for (const v of vals) {
    if (v == null) continue;
    const n = (counts.get(v) || 0) + 1;
    counts.set(v, n);
    if (n > bestN) { bestN = n; best = v; }
  }
  return best;
}

/**
 * Build advisory notices for one selected file against the set. Non-blocking. Covers:
 *  - non-WAV recommendation,
 *  - WAV sample-rate / bit-depth mismatch vs the set (header-read),
 *  - file-size-as-length proxy vs the set (soft; worded as a question).
 *
 * @param file      the file being added
 * @param header    its WAV header (null if not WAV / unreadable)
 * @param setStems  the stems already in the set (empty for a brand-new set)
 */
export function buildPrecheckNotices(
  file: File,
  header: WavHeader | null,
  setStems: SetStemRef[]
): PrecheckNotice[] {
  const notices: PrecheckNotice[] = [];

  if (!isWav(file.name)) {
    notices.push({
      severity: 'info',
      message: `${file.name} isn't a WAV. We recommend WAV for stems (lossless, exact length). It'll still upload, but format checks are limited until after analysis.`,
    });
    return notices; // no header to compare for non-WAV; stop here
  }

  // WAV header vs the set (only when the set has known values to compare against).
  const expectedSr = mode(setStems.map((s) => s.sampleRateHz));
  const expectedBd = mode(setStems.map((s) => s.bitDepth));
  if (header) {
    if (expectedSr != null && header.sampleRateHz !== expectedSr) {
      notices.push({
        severity: 'warn',
        message: `${file.name}: ${(header.sampleRateHz / 1000).toFixed(1)} kHz differs from the set (${(expectedSr / 1000).toFixed(1)} kHz).`,
      });
    }
    if (expectedBd != null && header.bitDepth !== expectedBd) {
      notices.push({
        severity: 'warn',
        message: `${file.name}: ${header.bitDepth}-bit differs from the set (${expectedBd}-bit).`,
      });
    }
  }

  // File-size-as-length proxy: for WAV of the same SR/bit-depth/channels, bytes ≈ duration.
  // Estimate the set's expected size for THIS file's format from a reference stem's bytes/ms,
  // then flag if the actual size is well off (>15% — looser than the post-ingest 5%, since this
  // is a rough proxy that can't see the true duration yet).
  const ref = setStems.find(
    (s) => s.durationMs && s.sizeBytes && s.sampleRateHz && s.bitDepth && s.channels
  );
  if (ref && header && ref.durationMs && ref.sizeBytes) {
    // Scale the reference's bytes/ms to this file's format (SR × bitDepth × channels).
    const refBytesPerMs = ref.sizeBytes / ref.durationMs;
    const formatScale =
      (header.sampleRateHz / (ref.sampleRateHz || header.sampleRateHz)) *
      (header.bitDepth / (ref.bitDepth || header.bitDepth)) *
      ((header.channels || 1) / (ref.channels || header.channels || 1));
    const expectedBytesPerMs = refBytesPerMs * formatScale;
    if (expectedBytesPerMs > 0) {
      const impliedMs = file.size / expectedBytesPerMs;
      const longestMs = Math.max(...setStems.map((s) => s.durationMs || 0), 0);
      if (longestMs > 0) {
        const delta = Math.abs(impliedMs - longestMs) / longestMs;
        if (delta > 0.15) {
          const shorter = impliedMs < longestMs;
          notices.push({
            severity: 'warn',
            message:
              `${file.name} looks ${shorter ? 'shorter' : 'longer'} than the other stems by file size. ` +
              `Is it meant to match their length and start point? (Confirmed after analysis.)`,
          });
        }
      }
    }
  }

  return notices;
}
